<template>
	<div class="text-center" v-if="addParticipantModal">
		<v-dialog v-model="addParticipantModal" max-width="520" scrollable persistent>
			<v-card class="padd-modal-card">

				<!-- ─── Header ──────────────────────────────────────── -->
				<div class="padd-header">
					<div class="padd-header-left">
						<div class="padd-header-icon">
							<v-icon size="18" color="#2D4B95">mdi-account-multiple-plus-outline</v-icon>
						</div>
						<div>
							<p class="padd-title">Agregar Participantes</p>
							<p class="padd-sub" v-if="textComponent">{{ textComponent }}</p>
						</div>
					</div>
					<button class="padd-close-btn" @click="closeModalSelf()">
						<v-icon size="18" color="#6B7280">mdi-close</v-icon>
					</button>
				</div>

				<!-- ─── Body ────────────────────────────────────────── -->
				<v-card-text class="pa-0">
					<div class="padd-body">

						<!-- Buscador -->
						<v-autocomplete
							v-model="selectedToAdd"
							:loading="$store.usersStore.state.loadingUserListForItems"
							:items="availableUsers"
							:item-disabled="isPresentParticipant"
							item-text="name"
							single-line
							outlined
							dense
							hide-details
							placeholder="Buscar usuario..."
							prepend-inner-icon="mdi-magnify"
							return-object
							clearable
							@change="onUserSelected"
						>
							<template v-slot:item="data">
								<v-list-item-avatar size="32">
									<img v-if="data.item.info && data.item.info.avatar" :src="data.item.info.avatar">
									<div v-else class="padd-avatar-fallback">{{ initials(data.item.name) }}</div>
								</v-list-item-avatar>
								<v-list-item-content>
									<v-list-item-title>
										{{ data.item.name }}
										<span class="padd-job-tag" v-if="data.item.projectjob && data.item.projectjob.userjobtype">{{ data.item.projectjob.userjobtype.name }}</span>
									</v-list-item-title>
									<v-list-item-subtitle>
										{{ data.item.email }}
										<small class="padd-already-tag" v-if="isPresentParticipant(data.item)"> · Ya participa</small>
									</v-list-item-subtitle>
								</v-list-item-content>
							</template>
						</v-autocomplete>

						<!-- Lista de seleccionados -->
						<div v-if="participants.length" class="participant-list">
							<p class="participant-list-label">Seleccionados ({{ participants.length }})</p>
							<div v-for="(p, i) in participants" :key="p.id" class="participant-card">
								<div class="participant-card-left">
									<div class="participant-avatar">
										<img v-if="p.info && p.info.avatar" :src="p.info.avatar" />
										<span v-else>{{ initials(p.name) }}</span>
									</div>
									<div class="participant-card-info">
										<span class="participant-card-name">{{ p.name }}</span>
										<span class="participant-card-role" v-if="p.projectjob && p.projectjob.userjobtype">{{ p.projectjob.userjobtype.name }}</span>
									</div>
								</div>
								<div class="participant-card-right">
									<!-- Fecha inicial -->
									<v-menu
										v-if="showDateSelectors"
										v-model="startDateMenus[p.id]"
										:close-on-content-click="false"
										max-width="290"
										min-width="290"
										offset-y
										nudge-bottom="4"
									>
										<template v-slot:activator="{ on, attrs }">
											<button class="participant-date-btn participant-date-btn--start" type="button" v-bind="attrs" v-on="on">
												<v-icon size="14" color="#6B7280">mdi-calendar-start</v-icon>
												<span>{{ participantStartDates[p.id] || 'Inicio' }}</span>
												<v-icon v-if="participantStartDates[p.id]" size="12" color="#9CA3AF" @click.stop="clearStartDate(p.id)">mdi-close</v-icon>
											</button>
										</template>
										<v-date-picker
											locale="es"
											:value="participantStartDates[p.id]"
											no-title
											@input="setStartDate(p.id, $event)"
										/>
									</v-menu>
									<!-- Fecha límite -->
									<v-menu
										v-if="showDateSelectors"
										v-model="dateMenus[p.id]"
										:close-on-content-click="false"
										max-width="290"
										min-width="290"
										offset-y
										nudge-bottom="4"
									>
										<template v-slot:activator="{ on, attrs }">
											<button class="participant-date-btn" type="button" v-bind="attrs" v-on="on">
												<v-icon size="14" color="#6B7280">mdi-calendar-outline</v-icon>
												<span>{{ participantDates[p.id] || 'Límite' }}</span>
												<v-icon v-if="participantDates[p.id]" size="12" color="#9CA3AF" @click.stop="clearDate(p.id)">mdi-close</v-icon>
											</button>
										</template>
										<v-date-picker
											locale="es"
											:value="participantDates[p.id]"
											no-title
											@input="setDate(p.id, $event)"
										/>
									</v-menu>
									<button class="participant-remove-btn" type="button" @click="removeSelected(i, p.id)">
										<v-icon size="16" color="#9CA3AF">mdi-close</v-icon>
									</button>
								</div>
							</div>
						</div>

						<!-- Estado vacío -->
						<div v-else class="padd-empty">
							<v-icon color="#D1D5DB" size="36">mdi-account-multiple-outline</v-icon>
							<span>Selecciona usuarios para agregar</span>
						</div>

					</div>
				</v-card-text>

				<!-- ─── Actions ──────────────────────────────────────── -->
				<div class="padd-actions">
					<button class="padd-btn-cancel" :disabled="loading" @click="closeModalSelf()">Cancelar</button>
					<button
						class="padd-btn-confirm"
						:disabled="loading || !participants.length"
						@click="addParticipants()"
					>
						<v-progress-circular v-if="loading" indeterminate size="14" width="2" color="white" class="mr-1" />
						Agregar{{ participants.length ? ` (${participants.length})` : '' }}
					</button>
				</div>

			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import {addParticipant} from '@/helpers/api/tasks'
import {setRdiSubscribers} from '@/helpers/api/rdi'
import {addGlobalPaymentStatusParticipant} from '@/helpers/api/additionals'

export default {
	name: 'AddParticipantModal',
	props: {
		closeModal: { type: Function },
		addParticipantModal: { type: Boolean, default: false },
		addingToType: { type: Number, default: 1 }, // 1: Tarea, 2: RDI, 3: AGPS
		item: { type: Object, required: true },
		showDateSelectors: { type: Boolean, default: true },
	},
	computed: {
		textComponent() {
			if (this.addingToType == 1) return 'a la Tarea'
			if (this.addingToType == 2) return 'a la RDI'
			if (this.addingToType == 3) return 'al Estado de Pago'
			return ''
		},
		availableUsers() {
			const selectedIds = new Set(this.participants.map(p => p.id))
			return (this.$store.usersStore.state.usersListForItems.users || []).filter(u => !selectedIds.has(u.id))
		},
	},
	watch: {
		addParticipantModal(newData) {
			if (newData) {
				if (this.addingToType === 1 || this.addingToType === 3) {
					this.presentParticipants = this.item.participants
					this.projectID = this.item.project_id
				} else if (this.addingToType === 2) {
					this.presentParticipants = this.item.users_suscriptions.filter(p => !p.deleted_at && p.email).map(user => ({ id: user.user_id, name: user.name, email: user.email, info: { avatar: user.avatar }, projectjob: { userjobtype: { name: user.job_type_name } } }))
					this.projectID = this.item.project_id
				}
				this.checkUsers()
			}
		}
	},
	data: () => ({
		loading: false,
		participants: [],
		participantDates: {},
		participantStartDates: {},
		dateMenus: {},
		startDateMenus: {},
		selectedToAdd: null,
		presentParticipants: [],
		projectID: null,
	}),
	mounted() {},
	methods: {
		initials(name) {
			return (name || '').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('')
		},
		isPresentParticipant(user) {
			return this.presentParticipants.some(p => user.id === p.id) ||
				this.item.responsable === user.id ||
				this.item.created_by === user.id
		},
		onUserSelected(user) {
			if (!user) return
			if (!this.participants.some(p => p.id === user.id)) {
				this.participants.push(user)
				this.$set(this.participantDates, user.id, null)
				this.$set(this.participantStartDates, user.id, null)
				this.$set(this.dateMenus, user.id, false)
				this.$set(this.startDateMenus, user.id, false)
			}
			this.$nextTick(() => { this.selectedToAdd = null })
		},
		removeSelected(index, userId) {
			this.participants.splice(index, 1)
			this.$delete(this.participantDates, userId)
			this.$delete(this.participantStartDates, userId)
			this.$delete(this.dateMenus, userId)
			this.$delete(this.startDateMenus, userId)
		},
		setDate(userId, date) {
			this.$set(this.participantDates, userId, date)
			this.$set(this.dateMenus, userId, false)
		},
		clearDate(userId) {
			this.$set(this.participantDates, userId, null)
		},
		setStartDate(userId, date) {
			this.$set(this.participantStartDates, userId, date)
			this.$set(this.startDateMenus, userId, false)
		},
		clearStartDate(userId) {
			this.$set(this.participantStartDates, userId, null)
		},
		checkUsers() {
			if (!this.$store.usersStore.state.loadingUserListForItems &&
				this.$store.usersStore.state.usersListForItems.project_id !== this.projectID) {
				this.getUserListforItems()
			}
		},
		getUserListforItems() {
			const data = { project_id: this.projectID, start: 0, limit: 9999 }
			this.$store.usersStore.dispatch('getUsersListForItems', data)
		},
		closeModalSelf() {
			this.participants = []
			this.participantDates = {}
			this.participantStartDates = {}
			this.dateMenus = {}
			this.startDateMenus = {}
			this.selectedToAdd = null
			this.closeModal()
		},
		addParticipants() {
			const participantData = this.participants.map(p => ({
				id: p.id,
				start_date: this.participantStartDates[p.id] || null,
				limit_date: this.participantDates[p.id] || null,
			}))
			if (this.addingToType == 1) {
				this.addParticipantTask(participantData)
			} else if (this.addingToType == 2) {
				this.addParticipantRdi(participantData)
			} else if (this.addingToType == 3) {
				this.addParticipantAgps(participantData)
			}
		},
		async addParticipantTask(participantData) {
			const data = { task_id: this.item.id, participants: participantData }
			this.loading = true
			const response = await addParticipant(data)
			this.loading = false
			if (response.code === 200) {
				this.$store.consultasStore.commit('setCurrentTarea', response.task)
				this.closeModalSelf()
			}
		},
		async addParticipantRdi(participantData) {
			const data = { rdi_id: this.item.id, subscribers: participantData.map(p => p.id) }
			this.loading = true
			const response = await setRdiSubscribers(data)
			this.loading = false
			if (response.code === 200) {
				this.$emit('reloadInfo')
				this.closeModalSelf()
			}
		},
		async addParticipantAgps(participantData) {
			const data = {
				additional_global_payment_status_id: this.item.id,
				participants: participantData,
			}
			this.loading = true
			const response = await addGlobalPaymentStatusParticipant(data)
			this.loading = false
			if (response.code === 200) {
				this.$store.adicionalesStore.dispatch('getAdditionalInformativeTable', {
					project_id: this.projectID,
					page: 1,
				})
				this.closeModalSelf()
			}
		},
	},
};
</script>

<style scoped>
/* ─── Modal ──────────────────────────────────────────── */
.padd-modal-card { border-radius: 14px !important; overflow: hidden; }

/* ─── Header ─────────────────────────────────────────── */
.padd-header {
	display: flex; align-items: center; justify-content: space-between;
	padding: 16px 20px; background: #F4F6FB; border-bottom: 1px solid #E0E5F0;
}
.padd-header-left { display: flex; align-items: center; gap: 10px; }
.padd-header-icon {
	width: 34px; height: 34px; border-radius: 9px;
	background: #E8EEFA; border: 1px solid #C8D4F0;
	display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.padd-title  { font-size: 15px; font-weight: 600; color: #1A1D23; margin: 0; line-height: 1.3; }
.padd-sub    { font-size: 12px; color: #6B7280; margin: 2px 0 0; }
.padd-close-btn {
	width: 28px; height: 28px; border: none; background: #EEEFF2;
	border-radius: 7px; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.padd-close-btn:hover { background: #E0E1E5; }

/* ─── Body ───────────────────────────────────────────── */
.padd-body { padding: 16px 20px; display: flex; flex-direction: column; gap: 14px; }

/* ─── Fallback avatar en dropdown ────────────────────── */
.padd-avatar-fallback {
	width: 32px; height: 32px; border-radius: 50%; background: #EEF2FF;
	color: #2D4B95; display: flex; align-items: center; justify-content: center;
	font-size: 11px; font-weight: 600;
}
.padd-job-tag     { color: #5E85EF; font-size: 12px; margin-left: 4px; }
.padd-already-tag { color: #9CA3AF; }

/* ─── Empty ──────────────────────────────────────────── */
.padd-empty {
	display: flex; flex-direction: column; align-items: center; gap: 8px;
	padding: 24px 0; color: #9CA3AF; font-size: 13px; text-align: center;
}

/* ─── Actions ────────────────────────────────────────── */
.padd-actions {
	display: flex; align-items: center; justify-content: flex-end; gap: 8px;
	padding: 12px 20px; border-top: 1px solid #E5E8EF; background: white;
}
.padd-btn-cancel {
	padding: 8px 14px; border: 1px solid #E5E8EF; border-radius: 8px;
	background: white; font-size: 13px; color: #374151; cursor: pointer; transition: background 0.15s;
}
.padd-btn-cancel:hover:not(:disabled) { background: #F3F4F6; }
.padd-btn-confirm {
	display: inline-flex; align-items: center; gap: 6px;
	padding: 8px 16px; border: none; border-radius: 8px;
	background: #2D4B95; color: white; font-size: 13px; font-weight: 600;
	cursor: pointer; transition: background 0.15s;
}
.padd-btn-confirm:hover:not(:disabled) { background: #243d7a; }
.padd-btn-confirm:disabled, .padd-btn-cancel:disabled { opacity: 0.55; cursor: not-allowed; }

/* ─── Participant card (compartido con CreateTareaModal) ─ */
.participant-list { display: flex; flex-direction: column; gap: 6px; }
.participant-list-label {
	font-size: 11px; font-weight: 600; color: #6B7280;
	text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 4px;
}
.participant-card {
	display: flex; align-items: center; justify-content: space-between;
	padding: 8px 10px; border: 1px solid #E5E8EF; border-radius: 8px;
	background: #F9FAFB; gap: 8px;
}
.participant-card-left  { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; }
.participant-avatar {
	width: 32px; height: 32px; border-radius: 50%;
	background: #EEF2FF; color: #2D4B95; flex-shrink: 0;
	display: flex; align-items: center; justify-content: center;
	font-size: 11px; font-weight: 600; overflow: hidden;
}
.participant-avatar img { width: 100%; height: 100%; object-fit: cover; }
.participant-card-info  { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.participant-card-name  { font-size: 13px; font-weight: 500; color: #1A1D23; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.participant-card-role  { font-size: 11px; color: #6B7280; }
.participant-card-right { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.participant-date-btn {
	display: inline-flex; align-items: center; gap: 4px;
	padding: 4px 8px; border: 1px solid #E5E8EF; border-radius: 6px;
	background: white; font-size: 11px; color: #6B7280; cursor: pointer;
	transition: all 0.15s; white-space: nowrap;
}
.participant-date-btn:hover { border-color: #2D4B95; color: #2D4B95; }
.participant-date-btn--start {
	background: #F0F4FF; border-color: #D4DCF7;
}
.participant-date-btn--start:hover { border-color: #2D4B95; color: #2D4B95; background: #E8EEFA; }
.participant-remove-btn {
	width: 24px; height: 24px; border: none; background: transparent;
	cursor: pointer; border-radius: 4px; display: flex; align-items: center; justify-content: center;
	transition: background 0.15s;
}
.participant-remove-btn:hover { background: #FEE2E2; }
</style>